/**
 * The wall as the workspace view (Part 06 decision 2). Card rects come from
 * the masonry's layout model (virtualized cards included); Pinned row tiles
 * from DOM anchors (`lib/frame-anchors.ts`). An agent working on a card
 * scrolled out of view rests at the wall's visible edge on that side, at the
 * card's column centre; while the card's page is not loaded, at the bottom
 * edge's centre (Part 07 decision 8). The wall scrolls only for
 * `bringIntoView` (Q2). Nothing reads the screen unless the wall shows in
 * this window (W5).
 */
import type { FrameScreenRect } from "@/components/cursors/frame-cursor-geometry";
import { frameAnchorElement } from "@/lib/frame-anchors";
import type { FocusCardOrigin } from "@/lib/focus/focus-types";
import { openFramesById } from "@/lib/open-frames";
import { exitPresentationMode } from "@/lib/presentation-mode";
import { flashWallArrival } from "@/lib/wall-arrivals";
import {
  isNeverOnWall,
  wallPlacement,
  wallToolsRow,
  type WallContext,
} from "@/lib/wall-membership";
import { wallVisibleHere } from "@/lib/wall-visibility";
import type {
  WorkspaceTransform,
  WorkspaceViewAdapter,
} from "@/lib/workspace-view-adapter";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { useFramesStore, type FramesState } from "@/stores/frames-store";
import { selectWallContext } from "@/stores/wall-feed-store";
import { WALL_HEADER_HEIGHT, type WallLayout } from "./wall-layout";
import { cardsNearView } from "./wall-visible-cards";

/** What `WallMasonry` registers on mount. */
export interface WallSurface {
  layout: () => WallLayout | null;
  scroller: () => HTMLElement | null;
  /** The element the layout's boxes are placed in. */
  canvas: () => HTMLElement | null;
  /** Centre a laid-out card in the view; false when it is not laid out. */
  scrollToCard: (frameId: string) => boolean;
  /** Whether the layout is the project feed without a search. */
  showsProjectFeed: () => boolean;
  /** Back to the project feed without a search, at its top. */
  showTop: () => void;
}

const IDENTITY: WorkspaceTransform = { x: 0, y: 0, zoom: 1 };
/** A cursor resting at the wall's edge stands this far inside it. */
const EDGE_CURSOR_INSET = 12;
/** Scrolling counts as settled after this many unchanged frames. */
const SETTLED_FRAMES = 2;
/** Give up waiting for a scroll after about a second. */
const MAX_SCROLL_FRAMES = 60;

let surface: WallSurface | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** `WallMasonry` mounts: its layout and scroller; returns the unregister. */
export function registerWallSurface(next: WallSurface): () => void {
  surface = next;
  const scroller = next.scroller();
  scroller?.addEventListener("scroll", notify, { passive: true });
  notify();
  return () => {
    scroller?.removeEventListener("scroll", notify);
    if (surface !== next) return;
    surface = null;
    notify();
  };
}

/** The masonry laid its cards out again. */
export function notifyWallLayout(): void {
  notify();
}

/**
 * Calls `listener` whenever the wall lays its cards out, scrolls, mounts
 * or unmounts; returns the unsubscribe.
 */
export function subscribeWallView(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The masonry cards showing in the wall's view here, below its header,
 * then those within one more view below it (`cardsNearView`).
 */
export function wallCardIdsNearView(): string[] {
  const layout = surface?.layout();
  const scroller = surface?.scroller();
  const canvas = surface?.canvas();
  if (!layout || !scroller || !canvas || !wallVisibleHere()) return [];
  const top = scroller.scrollTop - canvas.offsetTop;
  return cardsNearView(
    layout,
    top + WALL_HEADER_HEIGHT,
    top + scroller.clientHeight,
  );
}

function cardRect(frameId: string): FrameScreenRect | null {
  const box = surface?.layout()?.boxes.get(frameId);
  const scroller = surface?.scroller();
  const canvas = surface?.canvas();
  if (!box || !scroller || !canvas) return null;
  const view = scroller.getBoundingClientRect();
  return {
    left: view.left + canvas.offsetLeft + box.x - scroller.scrollLeft,
    top: view.top + canvas.offsetTop + box.y - scroller.scrollTop,
    width: box.width,
    height: box.height,
  };
}

/** The wall's scroller box below its sticky header, which covers the top. */
function visibleView(): {
  top: number;
  bottom: number;
  left: number;
  right: number;
} | null {
  const scroller = surface?.scroller();
  if (!scroller) return null;
  const view = scroller.getBoundingClientRect();
  return {
    top: view.top + WALL_HEADER_HEIGHT,
    bottom: view.bottom,
    left: view.left,
    right: view.right,
  };
}

/** Whether any of `rect` shows within the wall's scroller. */
function inView(rect: FrameScreenRect): boolean {
  const view = visibleView();
  if (!view) return false;
  return (
    rect.top < view.bottom &&
    rect.top + rect.height > view.top &&
    rect.left < view.right &&
    rect.left + rect.width > view.left
  );
}

function fullyInView(rect: FrameScreenRect): boolean {
  const view = visibleView();
  if (!view) return false;
  return rect.top >= view.top && rect.top + rect.height <= view.bottom;
}

/**
 * A card of the masonry showing in the wall's view here: where an opening
 * grows from and its return lands (Part 10 Rule 3). Null for Pinned tiles,
 * off-screen cards and a hidden wall.
 */
export function visibleWallCard(frameId: string): FocusCardOrigin | null {
  if (!wallVisibleHere()) return null;
  const rect = cardRect(frameId);
  const fontSize = surface?.layout()?.geometry.fontSize;
  return rect && fontSize && inView(rect) ? { rect, fontSize } : null;
}

/** Whether a laid-out card shows whole in the wall's scroller. */
export function wallCardFullyInView(frameId: string): boolean {
  const card = cardRect(frameId);
  return !!card && fullyInView(card);
}

function tileRect(frameId: string): DOMRect | null {
  const element = frameAnchorElement("tile", frameId);
  if (!element?.isConnected) return null;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0 ? rect : null;
}

/**
 * A zero-size rect at the wall's visible edge on `rect`'s side, at its
 * horizontal centre; with no rect (its page is not loaded), at the bottom
 * edge's centre.
 */
function besideWallEdge(rect: FrameScreenRect | null): FrameScreenRect | null {
  const view = visibleView();
  if (!view) return null;
  const above = !!rect && rect.top + rect.height <= view.top;
  return {
    left: rect ? rect.left + rect.width / 2 : (view.left + view.right) / 2,
    top: above ? view.top + EDGE_CURSOR_INSET : view.bottom - EDGE_CURSOR_INSET,
    width: 0,
    height: 0,
  };
}

/** Whether the wall can show `frameId` once its page loads. */
function belongsOnWall(frameId: string): boolean {
  const frame = useFramesStore.getState().framesById[frameId];
  return !!frame && !frame.archivedAt && !isNeverOnWall(frame);
}

function wallFrameRect(frameId: string): FrameScreenRect | null {
  const card = cardRect(frameId);
  if (card && inView(card)) return card;
  const tile = tileRect(frameId);
  if (tile && inView(tile)) return tile;
  const box = card ?? tile;
  if (!box && !belongsOnWall(frameId)) return null;
  return besideWallEdge(box);
}

function nextFrame(signal?: AbortSignal): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(false);
      return;
    }
    requestAnimationFrame(() => resolve(!signal?.aborted));
  });
}

/**
 * Resolves once the target shows in the view and the scroller stops moving,
 * plus one settled frame. A smooth scroll may start a frame late, so a still
 * scroller only counts once the target has arrived.
 */
async function scrollSettled(
  scroller: HTMLElement,
  arrived: () => boolean,
  signal?: AbortSignal,
): Promise<boolean> {
  let last = scroller.scrollTop;
  let still = 0;
  for (let frame = 0; frame < MAX_SCROLL_FRAMES; frame += 1) {
    if (!(await nextFrame(signal))) return false;
    const current = scroller.scrollTop;
    still = current === last && arrived() ? still + 1 : 0;
    last = current;
    if (still >= SETTLED_FRAMES) break;
  }
  return nextFrame(signal);
}

/**
 * Whether every frame in `frameIds` has its place on the project feed: a
 * Pinned row tile in the tools feed or a card in the layout model
 * (virtualized cards included). Frames the wall never shows (chats,
 * archived) are skipped; one the store has not loaded yet is still on its
 * way. The membership context is rebuilt only when the frames change.
 */
function framesPlacedCheck(frameIds: readonly string[]): () => boolean {
  let memo: {
    state: Pick<FramesState, "framesById" | "frameOrder">;
    ctx: WallContext;
    toolsRow: ReadonlySet<string>;
  } | null = null;
  return () => {
    const state = useFramesStore.getState();
    if (!surface?.showsProjectFeed()) return false;
    if (frameIds.some((id) => !state.framesById[id])) return false;
    if (
      memo?.state.framesById !== state.framesById ||
      memo.state.frameOrder !== state.frameOrder
    ) {
      const ctx = selectWallContext(state);
      memo = { state, ctx, toolsRow: new Set(wallToolsRow(ctx)) };
    }
    const { ctx, toolsRow } = memo;
    const boxes = surface.layout()?.boxes;
    return frameIds.every(
      (id) =>
        toolsRow.has(id) ||
        boxes?.has(id) ||
        wallPlacement(state.framesById[id]!, ctx) !== "wall",
    );
  };
}

/** Whether a frame's card or Pinned row tile shows in the wall's view. */
function shownInView(frameId: string): boolean {
  const card = cardRect(frameId);
  if (card) return inView(card);
  const tile = tileRect(frameId);
  return !!tile && inView(tile);
}

/**
 * Resolves once `done()` holds, checking on wall layout and scroll, frames
 * store and desktop mode changes; rejects with `signal.reason` on abort.
 */
function waitUntil(done: () => boolean, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const stops: Array<() => void> = [];
    const stop = () => {
      for (const unsubscribe of stops) unsubscribe();
      signal.removeEventListener("abort", aborted);
    };
    const aborted = () => {
      stop();
      reject(signal.reason);
    };
    const check = () => {
      if (!done()) return;
      stop();
      resolve();
    };
    listeners.add(check);
    stops.push(
      () => listeners.delete(check),
      useFramesStore.subscribe(check),
      useDesktopMode.subscribe(check),
    );
    signal.addEventListener("abort", aborted, { once: true });
    check();
  });
}

async function settleWallFrames(
  frameIds: readonly string[],
  signal: AbortSignal,
): Promise<void> {
  // The wall shows here once denk mode's native transition has finished.
  await waitUntil(
    () =>
      !!surface &&
      wallVisibleHere() &&
      !useDesktopMode.getState().nativeGeometryTransitioning,
    signal,
  );
  surface?.showTop();
  await waitUntil(framesPlacedCheck(frameIds), signal);
  // One frame for the cards the scroll and layout mount.
  if (!(await nextFrame(signal))) throw signal.reason;
  flashWallArrival(frameIds.filter(shownInView));
}

export const wallViewAdapter: WorkspaceViewAdapter = {
  kind: "wall",

  frameRect: (frameId) => (wallVisibleHere() ? wallFrameRect(frameId) : null),

  subscribe(listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  async bringIntoView(frameId, signal) {
    const scroller = surface?.scroller();
    if (!wallVisibleHere() || !surface || !scroller) return false;
    const card = cardRect(frameId);
    if (card) {
      if (fullyInView(card)) return true;
      if (!surface.scrollToCard(frameId)) return false;
      return scrollSettled(
        scroller,
        () => {
          const rect = cardRect(frameId);
          return !!rect && inView(rect);
        },
        signal,
      );
    }
    const tile = frameAnchorElement("tile", frameId);
    if (!tile?.isConnected) return false;
    if (fullyInView(tile.getBoundingClientRect())) return true;
    tile.scrollIntoView({ block: "nearest" });
    return scrollSettled(
      scroller,
      () => inView(tile.getBoundingClientRect()),
      signal,
    );
  },

  settleFrames: settleWallFrames,

  async showOverview() {
    exitPresentationMode({ restoreViewport: false });
    surface?.showTop();
  },

  // The backend places new frames at the top of the wall.
  prepareFrameArrival: () => {},

  transform: () => IDENTITY,
  useTransform: () => IDENTITY,
  toWorkspacePoint: (client) => client,
  // The backend places new work at the top of the wall.
  framePositions: () => undefined,
  viewportCenterHint: () => undefined,

  revealFrames(frameIds) {
    openFramesById({ frameIds, origin: "navigate" });
  },
  // Chats never show as cards.
  visibleConversationIds: () => [],
};
